'use client'

import { EIGENFIELD_VARIANTS, type EigenfieldVariantName, type TextBandFade } from '@roomtreatment/design'
import Eigenfield from './Eigenfield'

/**
 * A landing section with the generated field running behind it.
 *
 * The hero establishes the field; this carries it down the rest of the page so
 * it reads as the site's material rather than as one section's decoration. Each
 * section draws a DIFFERENT superposition (`EIGENFIELD_VARIANTS`) — four copies
 * of one picture is a tiled background, which is the opposite of a motif.
 *
 * The drawing's SIZE is not a variable and does not need to be one: `fieldScale`
 * divides each axis by its own reference dimension, so contour spacing is a
 * constant 21.6 CSS px on every canvas whatever its shape (`lineSpacingPx`).
 * A tall section shows more of the field, not a bigger one.
 *
 * The only shaping is the MASK: the field fades in at the top of the section and
 * out at the bottom, so it never meets a section border on a hard edge. That is
 * the hero's arrangement, and it is what makes consecutive sections read as one
 * continuous field seen through several windows rather than several backgrounds.
 *
 * ── Legibility, and why nothing is pooled here ───────────────────────────────
 * The hero pools paper under its copy (`FieldHalo`) because it draws the field
 * at 0.42 and its subhead would otherwise sit at 3.7:1. At texture strength no
 * pool is needed and none is used — an erased rectangle behind every paragraph
 * is plainly visible against a texture this quiet, and reads as a smudge rather
 * than as protection. It was built and thrown away for exactly that reason.
 *
 * What that costs is that the ink itself has to clear AA over the field, so two
 * marks moved a tier: the failure bands' index (`--rt-accent`, 4.60:1 bare) and
 * the FAQ numbering (`--rt-ink-faint`, 5.13:1 bare). Neither clears at ANY
 * opacity — they are under the bar before the field is drawn at all. Everything
 * else on these sections had headroom. `fieldSectionLegibility.test.ts` holds
 * the line, and `rendered-legibility.spec.ts` measures it in real pixels.
 */

/**
 * A texture, not a picture. The hero draws the field at 0.42 and is the page's
 * one loud statement; below it the same field is the paper's grain — present
 * enough that the page is visibly made of something, far too quiet to compete
 * with a paragraph.
 *
 * Held as a number rather than a Tailwind `opacity-[...]` class on purpose: the
 * ground a text pool has to match is derived from it just below, and a scanner
 * cannot read a JS constant, so the class form would mean writing the same
 * number twice and finding out later that only one of them moved.
 */
export const SECTION_FIELD_OPACITY = 0.12

/**
 * Feathered at both ends. Percentages rather than pixels so a short section
 * (the showcase) and a very tall one (three failure bands) both dissolve over a
 * proportional run instead of one of them showing a hard line.
 */
const SECTION_MASK =
  '[mask-image:linear-gradient(to_bottom,transparent_0%,black_14%,black_86%,transparent_100%)]'

/**
 * The shader's own thinning band, all but off.
 *
 * It exists for layouts where the art sits above the text and has to get out of
 * the way on the way down. Nothing on these sections is arranged that way, and a
 * second thinning mechanism would only make the live shader and the baked still
 * — which cannot honour this at all — disagree about the lower half of every
 * section.
 */
const SECTION_ART_FADE: TextBandFade = { start: 0.9, end: 1, minAlpha: 0.7 }

/**
 * What this section's ground actually is, once the field is over it.
 *
 * The canvas paints paper edge to edge, not just its lines, so a section with
 * the field behind it is tinted a few levels toward `--rt-paper`. Anything that
 * needs to paint that same ground has to read this rather than `--app-bg`, or it
 * shows up as a patch.
 *
 * Also what the showcase's tab strip paints itself with, so its chrome does not
 * cut a flat rectangle out of the tinted section around it.
 */
const FIELD_GROUND = `color-mix(in srgb, var(--rt-paper) ${Math.round(SECTION_FIELD_OPACITY * 100)}%, var(--app-bg))`

export default function FieldSection({
  variant,
  maxPixelRatio,
  className = '',
  children,
  ...rest
}: {
  variant: EigenfieldVariantName
  /** Forwarded to Eigenfield — see its prop doc. */
  maxPixelRatio?: number
  className?: string
  children: React.ReactNode
} & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children'>) {
  return (
    <section
      {...rest}
      data-field-section={variant}
      className={`relative overflow-hidden ${className}`}
      style={{ ['--field-ground' as string]: FIELD_GROUND }}
    >
      {/* Opacity and mask ride on the wrapper, not on the canvas: the canvas
          runs its own 0 -> 1 reveal on first frame, and two opacities on one
          element is how a 12% field briefly becomes a 100% one. */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 ${SECTION_MASK}`}
        style={{ opacity: SECTION_FIELD_OPACITY }}
      >
        <Eigenfield
          variant={EIGENFIELD_VARIANTS[variant]}
          maxPixelRatio={maxPixelRatio}
          artFade={SECTION_ART_FADE}
          className="h-full w-full"
        />
      </div>
      {/* Tagged so the legibility suite can take the content away and photograph
          the field on its own. Sampling around the glyphs instead reads their
          own antialiased edges, which is how a headline on solid paper measures
          1.5:1 (see rendered-legibility.spec.ts). */}
      {/* `isolate` is load-bearing. The pools sit at `z-index: -1` so a wide one
          cannot paint over a neighbouring block's words; without a stacking
          context here that -1 escapes the layer and lands behind the field
          instead, which hides the pools entirely. */}
      <div data-field-content className="relative isolate">
        {children}
      </div>
    </section>
  )
}
